import { Menu } from "lucide-react";
import React, { ReactNode, useId, useState } from "react";

import { cn } from "lib/utils";

export interface MaterialDetailsOrder {
	product_code?: string | number | null;
	product_description?: string | null;
	product?: {
		code?: string | number | null;
		description?: string | null;
		material_freight_grp?: string | null;
	} | null;
}

interface MaterialDetailsProps {
	order: MaterialDetailsOrder;
	children: ReactNode;
}

const displayValue = (value?: string | number | null) =>
	value === undefined || value === null || value === "" ? "--" : value;

export const MaterialDetails: React.FC<MaterialDetailsProps> = ({
	order,
	children,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const detailsId = useId();
	const productCode = order.product_code ?? order.product?.code;
	const description = order.product_description ?? order.product?.description;
	const isImo =
		order.product?.material_freight_grp?.trim().toUpperCase() === "IMO";
	const classification = isImo ? "IMO" : "--";

	return (
		<div className={cn("relative mb-5 w-full")}>
			<div className={cn("relative")}>
				{children}
				<button
					type="button"
					aria-expanded={isOpen}
					aria-controls={detailsId}
					aria-label={
						isOpen
							? "Ocultar detalhes do material"
							: "Exibir detalhes do material"
					}
					onClick={() => setIsOpen((current) => !current)}
					className={cn(
						"absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[#4b5a70]",
						"transition-colors hover:text-[#26364c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008bd2]",
					)}
				>
					<Menu
						className={cn(
							"h-5 w-5 transition-transform",
							isOpen && "rotate-180",
						)}
						aria-hidden="true"
					/>
				</button>
			</div>

			{isOpen && (
				<div
					id={detailsId}
					className={cn(
						"grid grid-cols-1 gap-4 border-t border-[#d3d9e1] bg-[#e4e7eb] px-6 py-4",
						"sm:grid-cols-3",
					)}
				>
					<div
						className={cn(
							"flex min-w-0 items-baseline gap-2 sm:flex-row",
						)}
					>
						<strong
							className={cn(
								"shrink-0 text-xs font-semibold uppercase tracking-wide text-[#26364c]",
							)}
						>
							Código do Produto
						</strong>
						<span className={cn("truncate text-sm text-[#596578]")}>
							{displayValue(productCode)}
						</span>
					</div>

					<div
						className={cn(
							"flex min-w-0 items-baseline gap-2 sm:flex-row",
						)}
					>
						<strong
							className={cn(
								"shrink-0 text-xs font-semibold uppercase tracking-wide text-[#26364c]",
							)}
						>
							Descrição
						</strong>
						<span className={cn("truncate text-sm text-[#596578]")}>
							{displayValue(description)}
						</span>
					</div>

					<div
						className={cn(
							"flex min-w-0 items-baseline gap-2 sm:flex-row",
						)}
					>
						<strong
							className={cn(
								"shrink-0 text-xs font-semibold uppercase tracking-wide text-[#26364c]",
							)}
						>
							Classificação do Item
						</strong>
						<span className={cn("text-sm text-[#596578]")}>
							{displayValue(classification)}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default MaterialDetails;
